<script setup>
import { storeToRefs } from 'pinia';
import { useExportGanttChart } from '~/project-management/composables/pm-export-gantt.composable';
import { getGanttColumns } from '~/project-management/constants/pm-gantt-column';
import { useProjectManagementStore } from '~/project-management/store/pm.store';

const emit = defineEmits(['close']);
const $t = inject('$t');
const pm_store = useProjectManagementStore();
const { active_schedule, active_view, show_overall_weights } = storeToRefs(pm_store);
const { setGanttExporting, setSelectedColumns } = useExportGanttChart();

const search = ref('');

// Flat list of all leaf columns — mirrors pm-customize-columns.vue logic
const all_columns = computed(() => {
  const groups = getGanttColumns($t, active_schedule.value);

  if (active_schedule.value.has_activity_weightages) {
    const general = groups.find(g => g.name === 'general');
    general?.children.push({
      name: show_overall_weights.value ? 'overall_weight' : 'weight',
      label: $t('Weight'),
      type: 'number',
      align: 'center',
      min_width: 80,
      width: 80,
    });
  }

  if (active_view.value.data?.baseline) {
    const dates = groups.find(g => g.name === 'dates');
    const durations = groups.find(g => g.name === 'durations');
    dates?.children.push(
      { name: 'bl_start', label: $t('Baseline Start'), type: 'date', align: 'center', min_width: 75 },
      { name: 'bl_finish', label: $t('Baseline Finish'), type: 'date', align: 'center', min_width: 80 },
    );
    durations?.children.push(
      { name: 'bl_duration', label: $t('Baseline Duration'), type: 'number', align: 'center', min_width: 90 },
    );
  }

  const activity_codes = active_schedule.value.activity_codes || [];
  if (activity_codes.length)
    groups.push({ name: 'activity_codes', label: $t('Activity Codes'), children: activity_codes.map(c => ({ name: `activity_code_${c.name}`, label: c.name, min_width: 65, width: 65 })) });

  const custom_fields = active_schedule.value.custom_fields || {};
  if (Object.keys(custom_fields).length)
    groups.push({ name: 'custom_fields', label: $t('Custom Fields'), children: Object.keys(custom_fields).map(c => ({ name: `custom_field_${c}`, label: c, min_width: 65, width: 65, align: 'center' })) });

  return groups.flatMap(g => g.children);
});

const filtered_columns = computed(() => {
  const q = search.value.trim().toLowerCase();
  return q ? all_columns.value.filter(c => c.label.toLowerCase().includes(q)) : all_columns.value;
});

const selected_names = ref(
  ['wbs', 'text', 'resources', 'planned_start', 'planned_finish', 'duration', 'progress'],
);

const all_filtered_selected = computed(() =>
  filtered_columns.value.length > 0 && filtered_columns.value.every(c => selected_names.value.includes(c.name)),
);

function toggleSelectAll() {
  if (all_filtered_selected.value)
    selected_names.value = selected_names.value.filter(n => !filtered_columns.value.some(c => c.name === n));
  else
    selected_names.value = [...new Set([...selected_names.value, ...filtered_columns.value.map(c => c.name)])];
}

function toggleColumn(name) {
  const idx = selected_names.value.indexOf(name);
  if (idx === -1)
    selected_names.value = [...selected_names.value, name];
  else selected_names.value = selected_names.value.filter(n => n !== name);
}

function onExport() {
  if (!selected_names.value.length)
    return;
  const selected_set = new Set(selected_names.value);
  // Preserve the modal list order (all_columns) rather than the toggle order
  const ordered_cols = all_columns.value.filter(c => selected_set.has(c.name));
  setSelectedColumns(ordered_cols);
  setGanttExporting(true);
  emit('close');
}
</script>

<template>
  <HawkModalContainer>
    <div class="col-span-12">
      <HawkModalHeader @close="emit('close')">
        <template #title>
          {{ $t('Select columns to export') }}
        </template>
      </HawkModalHeader>
      <HawkModalContent :is_scroll="false">
        <div class="flex items-center justify-between mb-3">
          <HawkSearchInput v-model="search" :full_width="true" :placeholder="$t('Search')" />
          <HawkButton type="text" class="ml-3 whitespace-nowrap flex-shrink-0" @click="toggleSelectAll">
            {{ all_filtered_selected ? $t('Deselect all') : $t('Select all') }}
          </HawkButton>
        </div>
        <div class="h-72 overflow-y-auto scrollbar border border-gray-200 rounded-lg px-3 py-2">
          <div v-if="!filtered_columns.length" class="text-sm text-gray-500 py-4 text-center">
            {{ $t('No results found') }}
          </div>
          <div
            v-for="col in filtered_columns"
            :key="col.name"
            class="flex items-center gap-2.5 py-1.5 cursor-pointer hover:bg-gray-50 rounded px-1"
            @click="toggleColumn(col.name)"
          >
            <HawkCheckbox
              tristate
              :model-value="selected_names.includes(col.name) ? 'checked' : 'unchecked'"
            />
            <span class="text-sm text-gray-700 select-none">{{ col.label }}</span>
          </div>
        </div>
      </HawkModalContent>
      <HawkModalFooter>
        <template #right>
          <div class="flex justify-end w-full gap-3">
            <HawkButton type="outlined" @click="emit('close')">
              {{ $t('Cancel') }}
            </HawkButton>
            <HawkButton color="primary" :disabled="!selected_names.length" @click="onExport">
              {{ $t('Export') }}
            </HawkButton>
          </div>
        </template>
      </HawkModalFooter>
    </div>
  </HawkModalContainer>
</template>
